import { useQueryClient } from "react-query";

import {
  DefaultButton,
  Dialog,
  IDialogProps,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { Selector } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  FieldOpsCacheKeys,
  useSelectorEvaluate
} from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { DeviceSummaryTable } from "./DeviceSummaryTable";

interface EvaluateSelectorDialogProps extends IDialogProps {
  onDismiss: () => void;
  selector: Selector;
}

export const EvaluateSelectorDialog: React.FC<EvaluateSelectorDialogProps> = ({
  onDismiss,
  selector,
  ...props
}) => {
  const theme = useTheme();

  const {
    data: evaluatedSelectorDevices,
    isLoading: isEvaluationLoading,
    error: evaluateError
  } = useSelectorEvaluate(selector.id);

  const queryClient = useQueryClient();
  const invalidateQueryCache = async () => {
    await queryClient.invalidateQueries([
      FieldOpsCacheKeys.SelectorEvaluation,
      selector.id
    ]);
  };

  return (
    <Dialog
      dialogContentProps={{
        showCloseButton: true,
        title: "Affected Devices"
      }}
      minWidth="80%"
      styles={{ main: { maxHeight: 600 } }}
      onDismiss={onDismiss}
      {...props}
    >
      <Stack tokens={{ childrenGap: theme.spacing.m }}>
        <DeviceSummaryTable
          items={evaluatedSelectorDevices ?? []}
          enableShimmer={isEvaluationLoading}
          errorMessage={evaluateError?.message}
          isSelectedOnFocus
        />

        <Stack
          horizontal
          tokens={{ childrenGap: theme.spacing.m }}
          styles={{ root: { justifyContent: "end" } }}
        >
          <Text
            styles={{
              root: {
                background: theme.semanticColors.listItemBackgroundChecked,
                width: "fit-content",
                height: "fit-content",
                padding: 2
              }
            }}
          >
            Total Devices: {evaluatedSelectorDevices?.length}
          </Text>

          <DefaultButton onClick={invalidateQueryCache}>Reload</DefaultButton>
          <DefaultButton onClick={onDismiss}>Cancel</DefaultButton>
        </Stack>
      </Stack>
    </Dialog>
  );
};
